
import { useState } from 'react';
import { showSuccess, showError } from "@/hooks/use-toast";
import { PaymentMethodType, PaymentContentType } from '../types/payment';
import { updateCampaignPaymentType } from '../utils/paymentUtils';
import { paymentScheduleService } from '@/hooks/campaign/services/paymentScheduleService';
import { secureStorage } from '@/utils/secureStorage';

export const usePaymentOptions = (campaignId: string, onPaymentComplete: () => Promise<void>) => {
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodType>('bank_transfer');
  const [paymentType, setPaymentType] = useState<PaymentContentType>('immediate');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showBankDetails, setShowBankDetails] = useState(false);
  const [showPaymentOptions, setShowPaymentOptions] = useState(true);
  const [showPaymentPlanSelector, setShowPaymentPlanSelector] = useState(false);
  const [showPurchaseOrder, setShowPurchaseOrder] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  
  // Tax Invoice state
  const [showTaxInvoiceForm, setShowTaxInvoiceForm] = useState(false);
  const [selectedPaymentMethodForInvoice, setSelectedPaymentMethodForInvoice] = useState<'creditcard' | 'bank_transfer'>('creditcard');
  
  const handlePaymentMethodChange = (method: PaymentMethodType) => {
    setPaymentMethod(method);
  };

  const handlePayNow = () => {
    // Clear PO flag when switching to immediate payment
    localStorage.removeItem('isPurchaseOrder');
    localStorage.removeItem('selectedPaymentPlan');
    setPaymentType('immediate');
    // Show tax invoice form instead of payment form
    setSelectedPaymentMethodForInvoice('creditcard');
    setShowTaxInvoiceForm(true);
    setShowPaymentOptions(false);
  };

  const handleBankTransfer = async () => {
    // Show payment plan selector instead of immediate bank transfer
    setShowPaymentPlanSelector(true);
    setShowPaymentOptions(false);
  };

  const handlePaymentPlanSelected = async (planType: 'full' | 'installments', installmentCount: number, dueDays: number) => {
    const effectiveCampaignId = campaignId || localStorage.getItem('currentCampaignId');
    
    if (!effectiveCampaignId) {
      console.error('Missing campaign ID for payment plan setup');
      showError({
        title: "Error",
        description: "Campaign information is missing. Please try again.",
      });
      return;
    }

    try {
      setIsConfirming(true);

      // Store payment plan selection
      localStorage.setItem('selectedPaymentPlan', JSON.stringify({
        type: planType,
        installmentCount,
        dueDays,
        timestamp: new Date().toISOString()
      }));
      
      // Mark as purchase order flow to prevent duplicate confirmation emails
      localStorage.setItem('isPurchaseOrder', 'true');

      // Check if this is a real UUID (from database) or a draft ID
      const isRealCampaign = effectiveCampaignId.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
      
      if (isRealCampaign) {
        // Update campaign payment type
        const { error: updateError } = await updateCampaignPaymentType(effectiveCampaignId);
        if (updateError) throw updateError;

        // Get total amount from secureStorage or localStorage
        const budgetData = secureStorage.get<any>('budgetCalculations') || (localStorage.getItem('budgetCalculations') ? JSON.parse(localStorage.getItem('budgetCalculations')!) : null);
        const totalAmount = budgetData?.totalBudget ?? budgetData?.totalAmount ?? 0;

        // Create payment schedule
        const firstDueDate = new Date();
        firstDueDate.setDate(firstDueDate.getDate() + dueDays);

        await paymentScheduleService.createPaymentSchedule({
          campaignId: effectiveCampaignId,
          totalAmount,
          planType,
          installmentCount,
          firstDueDate: firstDueDate.toISOString()
        });
      } else {
        // For draft campaigns, store payment plan locally
        localStorage.setItem('paymentPlan', JSON.stringify({
          type: planType,
          installmentCount,
          dueDays
        }));
        console.log('Payment plan selected for draft campaign:', { planType, installmentCount, dueDays });
      }

      // Show success message
      showSuccess({
        title: "Payment Plan Confirmed",
        description: `${planType === 'full' ? 'Full payment' : `${installmentCount}-installment`} plan selected. Proceeding to purchase order generation.`,
      });

      // Close payment plan selector and directly open purchase order
      setShowPaymentPlanSelector(false);
      setShowPurchaseOrder(true);
      
    } catch (error) {
      console.error('Error setting up payment plan:', error);
      showError({
        title: "Error",
        description: "There was an error setting up your payment plan. Please try again.",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handlePaymentPlanCancel = () => {
    // Clear PO flag when canceling payment plan
    localStorage.removeItem('isPurchaseOrder');
    setShowPaymentPlanSelector(false);
    setShowPaymentOptions(true);
  };

  const handlePurchaseOrderComplete = async () => {
    try {
      // Call the original payment completion handler
      await onPaymentComplete();
      setShowPurchaseOrder(false);
    } catch (error) {
      console.error('Error completing purchase order:', error);
      showError({
        title: "Error",
        description: "There was an error completing your purchase order. Please try again.",
      });
    }
  };

  const handlePurchaseOrderCancel = () => {
    // Clear PO flag when canceling purchase order
    localStorage.removeItem('isPurchaseOrder');
    localStorage.removeItem('selectedPaymentPlan');
    setShowPurchaseOrder(false);
    setShowPaymentOptions(true);
  };

  const handleBackToPaymentPlan = () => {
    setShowPurchaseOrder(false);
    setShowPaymentPlanSelector(true);
  };

  const handleBankTransferConfirm = async () => {
    // Ensure we have a campaign ID
    const effectiveCampaignId = campaignId || localStorage.getItem('currentCampaignId');
    
    if (!effectiveCampaignId) {
      const newCampaignId = `campaign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('currentCampaignId', newCampaignId);
      console.log('Generated campaign ID for bank transfer confirmation:', newCampaignId);
    }

    try {
      setIsConfirming(true);
      
      // Store campaign ID in localStorage to make sure it's available during payment completion
      const finalCampaignId = effectiveCampaignId || localStorage.getItem('currentCampaignId');
      localStorage.setItem('currentCampaignId', finalCampaignId!);
      console.log('Stored campaign ID in localStorage for bank transfer:', finalCampaignId);
      
      // Call the onPaymentComplete function which will mark the campaign as paid
      await onPaymentComplete();
      
      showSuccess({
        title: "Bank Transfer Selected",
        description: "You have 60 days to complete the payment. Please save the IBAN number for your reference.",
      });
    } catch (error) {
      console.error('Error confirming bank transfer:', error);
      showError({
        title: "Error",
        description: "There was an error confirming your bank transfer selection. Please try again.",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  const handleBankTransferCancel = () => {
    setShowBankDetails(false);
    setPaymentType('immediate');
    setShowPaymentOptions(true);
  };

  const handleConfirmPayment = async () => {
    if (isConfirming) return;

    console.log("Starting payment confirmation process...");
    setIsConfirming(true);
    
    try {
      console.log("Showing payment processing notification");
      showSuccess({
        title: "Payment Processing",
        description: "Your payment is being processed. Please wait...",
      });
      
      // Ensure we have a campaign ID
      const effectiveCampaignId = campaignId || localStorage.getItem('currentCampaignId');
      
      if (!effectiveCampaignId) {
        // Generate one as a last resort
        const newCampaignId = `campaign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('currentCampaignId', newCampaignId);
        console.log('Generated campaign ID for payment confirmation:', newCampaignId);
      }
      
      console.log("Campaign ID for payment:", effectiveCampaignId || localStorage.getItem('currentCampaignId'));
      
      // Store campaign ID in localStorage to ensure it's available during payment flow
      localStorage.setItem('currentCampaignId', effectiveCampaignId || localStorage.getItem('currentCampaignId')!);
      
      // Add a small delay to make the UI feedback more noticeable
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      console.log("Calling onPaymentComplete...");
      await onPaymentComplete();
      console.log("onPaymentComplete completed successfully");
      
    } catch (err) {
      console.error('Payment error:', err);
      showError({
        title: "Payment Error",
        description: "There was an error processing your payment. Please try again.",
      });
    } finally {
      setIsConfirming(false);
    }
  };

  // Tax Invoice handlers
  const handleTaxInvoiceComplete = () => {
    // After tax invoice is confirmed, proceed to payment form
    setShowTaxInvoiceForm(false);
    setShowPaymentForm(true);
  };

  const handleTaxInvoiceCancel = () => {
    setShowTaxInvoiceForm(false);
    setShowPaymentOptions(true);
  };

  return {
    paymentMethod,
    paymentType,
    showPaymentForm,
    showBankDetails,
    setShowBankDetails,
    showPaymentOptions,
    showPaymentPlanSelector,
    showPurchaseOrder,
    isConfirming,
    // Tax Invoice state
    showTaxInvoiceForm,
    selectedPaymentMethodForInvoice,
    // Handlers
    handlePaymentMethodChange,
    handlePayNow,
    handleBankTransfer,
    handlePaymentPlanSelected,
    handlePaymentPlanCancel,
    handlePurchaseOrderComplete,
    handlePurchaseOrderCancel,
    handleBackToPaymentPlan,
    handleBankTransferConfirm,
    handleBankTransferCancel,
    handleConfirmPayment,
    // Tax Invoice handlers
    handleTaxInvoiceComplete,
    handleTaxInvoiceCancel
  };
};
